import { useState, type CSSProperties, type ReactNode } from 'react'
import { AnimatePresence, Reorder, useDragControls } from 'framer-motion'
import type { Category } from '../lib/supabase'
import { takenName } from '../lib/categoryNames'
import { SheetPanel } from './SheetPanel'
import { CategoryPills } from './CategoryPills'
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, GripIcon } from './icons'
import { focusOnMount } from '../hooks/useAutoFocus'
import { useLatest } from '../hooks/useLatest'
import { wideBtn, wideSubmitBtn } from './sheetButtons'

interface Props {
  open: boolean
  categories: Category[]
  /** Items on the list (not the history) in a category. */
  countOf: (categoryId: string) => number
  tintFor: (categoryId: string | null) => string
  onAdd: (name: string) => Promise<unknown>
  onRename: (id: string, name: string) => Promise<unknown>
  /** Deletes the category; its items, history and recipe picks move to `moveTo` (null: Other). */
  onDelete: (id: string, moveTo: string | null) => Promise<unknown>
  onReorder: (ids: string[]) => Promise<unknown>
  onClose: () => void
}

// The database's answer to a name the list already has (categories_name_guard, 049).
const NAME_TAKEN = '23505'
const takenText = (name: string) => `You already have “${name}”.`

export function CategorySheet({ open, categories, countOf, tintFor, onAdd, onRename, onDelete, onReorder, onClose }: Props) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<Category | null>(null)

  const ordered = [...categories].sort((a, b) => a.sort_order - b.sort_order)
  const ids = ordered.map(c => c.id).join()
  // While dragging, the order lives here; the categories catch up on drop.
  const [order, setOrder] = useState(() => ordered.map(c => c.id))
  const [orderOf, setOrderOf] = useState(ids)
  if (orderOf !== ids) { setOrderOf(ids); setOrder(ids ? ids.split(',') : []) }
  const dragged = useLatest(order)
  function drop() {
    if (dragged.current.join() !== ids) void onReorder(dragged.current)
  }
  const byId = new Map(categories.map(c => [c.id, c]))

  const addTaken = name.trim() ? takenName(categories, name) : null

  async function handleAdd() {
    const clean = name.trim()
    if (!clean || addTaken) return
    setSaving(true)
    try {
      await onAdd(clean)
      setName('')
    } catch (err) {
      setAddError((err as { code?: string })?.code === NAME_TAKEN ? takenText(clean) : 'Couldn’t add it. Try again.')
    } finally {
      setSaving(false)
    }
  }

  function startEdit(cat: Category) {
    setEditingId(cat.id)
    setEditingName(cat.name)
    setEditError(null)
  }

  async function commitEdit() {
    const clean = editingName.trim()
    if (!editingId || !clean || clean === byId.get(editingId)?.name) { setEditingId(null); return }
    const taken = takenName(categories, clean, editingId)
    if (taken) { setEditError(takenText(taken.name)); return }
    try {
      await onRename(editingId, clean)
      setEditingId(null)
    } catch (err) {
      setEditError((err as { code?: string })?.code === NAME_TAKEN ? takenText(clean) : 'Couldn’t rename it. Try again.')
    }
  }

  function move(from: number, to: number) {
    if (to < 0 || to >= order.length) return
    const next = [...order]
    const [id] = next.splice(from, 1)
    next.splice(to, 0, id)
    setOrder(next)
    void onReorder(next)
  }

  return (
    <AnimatePresence>
      {open && (
        <SheetPanel onClose={onClose} maxHeight="80dvh" layout="scroll" ariaLabel="Categories" zIndex={12}>
          {deleting ? (
            <DeleteStep category={deleting} count={countOf(deleting.id)}
              others={categories.filter(c => c.id !== deleting.id)} tintFor={tintFor}
              onDelete={onDelete} onDone={() => setDeleting(null)} />
          ) : (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>Categories</h2>

              {order.length > 0 && (
                <Reorder.Group axis="y" values={order} onReorder={setOrder}
                  style={{ listStyle: 'none', background: 'var(--ml-bg)', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
                  {order.map((id, idx) => {
                    const cat = byId.get(id)
                    if (!cat) return null
                    return (
                      <CategoryRow key={id} cat={cat} index={idx} count={order.length}
                        onDrop={drop} onMove={to => move(idx, to)}>
                        {editingId === cat.id ? (
                          <>
                            <input
                              ref={focusOnMount}
                              value={editingName}
                              aria-label={`Name of ${cat.name}`}
                              onChange={e => { setEditingName(e.target.value); setEditError(null) }}
                              onKeyDown={e => { if (e.key === 'Enter') void commitEdit(); if (e.key === 'Escape') setEditingId(null) }}
                              onBlur={() => void commitEdit()}
                              style={{
                                flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
                                fontSize: 16, color: 'var(--ml-text)',
                              }}
                            />
                            <button
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => void commitEdit()}
                              aria-label="Save name"
                              style={iconBtn({ color: 'var(--ml-accent-fg)' })}
                            >
                              <CheckIcon size={18} />
                            </button>
                          </>
                        ) : (
                          <>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 16, color: 'var(--ml-text)' }}>{cat.name}</span>
                            <button onClick={() => startEdit(cat)} style={iconBtn({ opacity: 0.5 })} aria-label={`Rename ${cat.name}`}>
                              <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
                                <path d="M9.5 2.5L11.5 4.5L5 11H3V9L9.5 2.5Z"
                                  stroke="var(--ml-text)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button onClick={() => setDeleting(cat)} style={iconBtn({ opacity: 0.5 })} aria-label={`Delete ${cat.name}`}>
                              <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
                                <path d="M2 4h10M5 4V2.5h4V4M5.5 6.5v4M8.5 6.5v4M3 4l.8 7.5h6.4L11 4"
                                  stroke="var(--ml-destructive)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </>
                        )}
                      </CategoryRow>
                    )
                  })}
                </Reorder.Group>
              )}
              {editError && <p className="ml-caption" role="alert" style={{ marginTop: -12, marginBottom: 16 }}>{editError}</p>}

              {/* Add new category */}
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ml-text-secondary)', textTransform: 'uppercase', letterSpacing: 'var(--ml-label-tracking)', marginBottom: 10 }}>
                New category
              </p>
              <input
                value={name}
                onChange={e => { setName(e.target.value); setAddError(null) }}
                onKeyDown={e => { if (e.key === 'Enter') void handleAdd() }}
                placeholder="Category name"
                style={{
                  display: 'block', width: '100%', border: '0.5px solid var(--ml-separator)', borderRadius: 12,
                  padding: '13px 14px', fontSize: 16, background: 'var(--ml-bg)',
                  color: 'var(--ml-text)', outline: 'none',
                  marginBottom: 12, boxSizing: 'border-box',
                }}
              />
              {(addTaken || addError) && (
                <p className="ml-caption" role="alert" style={{ marginTop: -4, marginBottom: 12 }}>
                  {addTaken ? takenText(addTaken.name) : addError}
                </p>
              )}

              <button
                onClick={() => void handleAdd()}
                disabled={!name.trim() || !!addTaken || saving}
                style={wideSubmitBtn(Boolean(name.trim()) && !addTaken && !saving)}
              >
                {saving ? 'Adding…' : 'Add category'}
              </button>

              <button onClick={onClose} style={wideBtn}>Done</button>
            </>
          )}
        </SheetPanel>
      )}
    </AnimatePresence>
  )
}

const iconBtn = (extra: CSSProperties): CSSProperties => ({
  background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', display: 'flex', ...extra,
})

/** One category: dragged by its handle (the sheet still scrolls), or moved a step with the arrows. */
function CategoryRow({ cat, index, count, onDrop, onMove, children }: {
  cat: Category
  index: number
  count: number
  onDrop: () => void
  onMove: (to: number) => void
  children: ReactNode
}) {
  const controls = useDragControls()
  return (
    <Reorder.Item value={cat.id} dragListener={false} dragControls={controls} onDragEnd={onDrop}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px 6px 4px',
        borderBottom: index < count - 1 ? '0.5px solid var(--ml-separator)' : 'none',
        background: 'var(--ml-bg)', touchAction: 'pan-y',
      }}>
      <span className="ml-trip-edit__handle" aria-hidden="true" onPointerDown={e => controls.start(e)}><GripIcon /></span>
      {children}
      <button type="button" className="ml-text-btn" aria-label={`Move ${cat.name} up`} disabled={index === 0}
        style={{ opacity: index === 0 ? 0.3 : 0.6 }} onClick={() => onMove(index - 1)}><ArrowUpIcon /></button>
      <button type="button" className="ml-text-btn" aria-label={`Move ${cat.name} down`} disabled={index === count - 1}
        style={{ opacity: index === count - 1 ? 0.3 : 0.6 }} onClick={() => onMove(index + 1)}><ArrowDownIcon /></button>
    </Reorder.Item>
  )
}

/**
 * Asked before a category goes (no Undo: the answer is the safeguard, as with
 * End trip). Its items move where the member says — Other unless they pick — and
 * so do the ones bought before and the recipes' picks (delete_category, 049).
 */
function DeleteStep({ category, count, others, tintFor, onDelete, onDone }: {
  category: Category
  count: number
  others: Category[]
  tintFor: (categoryId: string | null) => string
  onDelete: (id: string, moveTo: string | null) => Promise<unknown>
  onDone: () => void
}) {
  const [moveTo, setMoveTo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  async function remove() {
    setBusy(true)
    setFailed(false)
    try {
      await onDelete(category.id, count > 0 ? moveTo : null)
      onDone()
    } catch {
      setFailed(true)
      setBusy(false)
    }
  }

  return (
    <div role="alertdialog" aria-modal="true" aria-labelledby="ml-cat-delete-title" aria-describedby="ml-cat-delete-text"
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h2 id="ml-cat-delete-title" className="ml-display ml-sheet-title">Delete “{category.name}”?</h2>
      {count > 0 ? (
        <>
          <p id="ml-cat-delete-text" className="ml-caption">
            {count === 1 ? '1 item on your list is in it.' : `${count} items on your list are in it.`} Move them to:
          </p>
          <CategoryPills categories={others} value={moveTo} tintFor={tintFor} onPick={setMoveTo}
            label="Move them to" none={{ label: 'Other', onPick: () => setMoveTo(null) }} />
          <p className="ml-caption">Things you’ve bought before and recipes move with them.</p>
        </>
      ) : (
        <p id="ml-cat-delete-text" className="ml-caption">Nothing on your list is in it.</p>
      )}
      {failed && <p className="ml-caption" role="alert">Couldn’t delete it. Try again.</p>}
      <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" onClick={onDone} disabled={busy}>Keep it</button>
      <button type="button" className="ml-btn ml-btn--secondary ml-btn--wide" onClick={() => void remove()} disabled={busy}>
        Delete category
      </button>
    </div>
  )
}
